import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { PageTitle } from "../../components/admin/AdminUi";
import CostCard from "../../components/admin/pricing/CostCard";
import BuildCreditsCard from "../../components/admin/pricing/BuildCreditsCard";
import VideoCreditsCard from "../../components/admin/pricing/VideoCreditsCard";
import PacksCard from "../../components/admin/pricing/PacksCard";
import { useCredits } from "../../context/CreditsContext";
import { api, errMsg } from "../../lib/api";

const FIELDS = ["base_cost_inr", "markup_pct", "model_credits", "plan_credits", "image_credits", "domain_credits", "domain_monthly_credits", "low_balance_credits",
  "video_fast_per_sec", "video_cinematic_per_sec", "music_credits"];
const toBody = (p) => ({
  ...Object.fromEntries(FIELDS.map((k) => [k, p[k]])),
  packs: p.packs.map(({ id, name, credits, tagline, popular, active }) => ({ id, name, credits, tagline, popular, active })),
});

const invalid = (d) => {
  const nums = [d.base_cost_inr, d.markup_pct, d.plan_credits, d.image_credits, d.domain_credits, d.domain_monthly_credits, d.low_balance_credits,
    d.video_fast_per_sec, d.video_cinematic_per_sec, d.music_credits, ...Object.values(d.model_credits), ...d.packs.map((x) => x.credits)];
  if (nums.some((n) => n === "" || Number.isNaN(Number(n)))) return "Fill in every number field before saving";
  if (Number(d.base_cost_inr) <= 0) return "Your Emergent cost must be more than ₹0";
  if (Object.values(d.model_credits).some((n) => n < 1)) return "Each AI model needs at least 1 credit per build";
  if (d.packs.some((x) => !x.name.trim() || x.credits < 1)) return "Every pack needs a name and at least 1 credit";
  if (!d.packs.some((x) => x.active !== false)) return "Keep at least one pack on sale";
  return null;
};

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useCredits();

  const load = (r) => { setData(r); setDraft(toBody(r)); };
  useEffect(() => { api.adminPricing().then(load).catch((e) => toast.error(errMsg(e))); }, []);

  if (!draft) return <div className="space-y-4">{[0, 1, 2].map((i) => <div key={i} className="h-48 rounded-3xl shimmer" />)}</div>;
  const set = (upd) => setDraft((d) => ({ ...d, ...upd }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(toBody(data));

  const save = async () => {
    const bad = invalid(draft);
    if (bad) { toast.error(bad); return; }
    setSaving(true);
    try { load(await api.adminSavePricing(draft)); refresh(); toast.success("Pricing saved — users see the new prices now"); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-pricing" className="pb-24">
      <PageTitle title="Pricing" desc="Set your cost, your markup, credit packs and how many credits each build uses." />
      <div className="space-y-6 max-w-5xl">
        <CostCard p={draft} set={set} />
        <BuildCreditsCard p={draft} set={set} models={data.models} usage={data.usage_30d} />
        <VideoCreditsCard p={draft} set={set} />
        <PacksCard p={draft} set={set} />
      </div>
      <div className="fixed bottom-0 left-0 md:left-60 right-0 z-30 border-t border-slate-200 bg-white/90 backdrop-blur px-4 sm:px-8 py-3 flex items-center justify-end gap-3">
        {dirty && <span className="text-sm text-amber-700" data-testid="pricing-unsaved">Unsaved changes</span>}
        {dirty && <button onClick={() => setDraft(toBody(data))} data-testid="pricing-reset-btn" className="h-10 px-4 rounded-full text-sm text-slate-600 hover:bg-slate-100">Discard</button>}
        <button onClick={save} disabled={!dirty || saving} data-testid="admin-pricing-save-btn"
          className="h-10 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-slate-700 disabled:opacity-40">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save pricing
        </button>
      </div>
    </div>
  );
}
